import React from 'react'
import { inject, observer } from 'mobx-react'
import { useRouter } from 'next/router'
import Loading from '../components/loading'
import Error from '../components/error'
import Consts from '../configs/consts'

export const Type = {
  NotProtected: 0, // ログイン不要
  CognitoUser: 1, // Cognito上でのログインが必要
  Protected: 2, // 要ログイン
  ProtectedAdmin: 3, // 管理者権限が必要
  ProtectedReadContractRequest: 4, // 契約リクエストの閲覧権限が必要
  ProtectedReadStampRequest: 5, // スタンプリクエストの閲覧権限が必要
  ProtectedReadContract: 6, // 契約の閲覧権限が必要
  ProtectedCreateContractRequest: 7, // 契約リクエストの作成権限が必要
  ProtectedCreateStampRequest: 8, // スタンプリクエストの作成権限が必要
  ProtectedCreateContract: 9, // 契約の作成権限が必要
  ProtectedEditTemplate: 10, // テンプレートの編集権限が必要
  ProtectedEditCommonClause: 11, // 共通条項の編集権限が必要
  ProtectedEditVariable: 12, // 変数の編集権限が必要
  ProtectedEditCorporation: 13, // 法人の編集権限が必要
  ProtectedEditContractForm: 14, // 契約書フォームの編集権限が必要
  ProtectedEditContractNotification: 15, // 契約通知の編集権限が必要
  ProtectedEditApprovalRoute: 16, // 承認ルートの編集権限が必要
  ProtectedEditEmail: 17, // メールの編集権限が必要
  ProtectedEditPassword: 18, // パスワードの編集権限が必要
  ProtectedEditUserName: 19, // ユーザー名の編集権限が必要
}

export const checkProtected = (user, type) => {
  if (type === Type.Protected) {
    return Boolean(user)
  }
  if (!user.role) {
    return false
  }
  switch (type) {
    case Type.ProtectedAdmin:
      return user.role.admin
    case Type.ProtectedReadContractRequest:
      return user.role.readContractRequest !== 'none'
    case Type.ProtectedReadStampRequest:
      return user.role.readStampRequest !== 'none'
    case Type.ProtectedReadContract:
      return user.role.readContract !== 'none'
    case Type.ProtectedCreateContractRequest:
      return user.role.createContractRequest
    case Type.ProtectedCreateStampRequest:
      return user.role.createStampRequest
    case Type.ProtectedCreateContract:
      return user.role.createContract
    case Type.ProtectedEditTemplate:
      return user.role.editTemplate
    case Type.ProtectedEditCommonClause:
      return user.role.editCommonClause
    case Type.ProtectedEditVariable:
      return user.role.editVariable
    case Type.ProtectedEditCorporation:
      return user.role.editCorporation
    case Type.ProtectedEditContractForm:
      return user.role.editContractForm
    case Type.ProtectedEditContractNotification:
      return user.role.editContractNotification
    case Type.ProtectedEditApprovalRoute:
      return user.role.editApprovalRoute
    case Type.ProtectedEditEmail:
      return user.role.editEmail
    case Type.ProtectedEditPassword:
      return user.role.editPassword
    case Type.ProtectedEditUserName:
      return user.role.editUserName
    default:
      return false
  }
}

export function withAuth(Page, type = Type.Protected) {
  if (type === undefined) console.error('invalid type')
  return inject('AuthStore')(
    observer((props) => {
      const { AuthStore } = props

      let needsCheck = true
      if (type === Type.NotProtected) {
        needsCheck = false
      } else {
        needsCheck = !Boolean(AuthStore.user)
      }

      const router = useRouter()
      const [loading, setLoading] = React.useState(needsCheck)
      const [error, setError] = React.useState(null)

      React.useEffect(() => {
        // ログインページに遷移
        async function gotoLogin(expired = false) {
          const currentPath = router.asPath
          const nextQuery = currentPath.indexOf('/login') === -1 ? `?next=${encodeURIComponent(currentPath)}` : ''
          await AuthStore.logout(expired)
          await router.replace(`/login${nextQuery}`)
        }

        // ログイン状態を確認
        async function checkUser() {
          const { success, error } = await AuthStore.checkUser()
          if (!success) {
            if (error.status === 403) {
              setLoading(false)
              setError({
                statusCode: 403,
                message: '現在のネットワーク環境からはサービスにアクセスできません。\nネットワーク環境をご確認のうえ、再度お試しください。',
              })
              return
            }
            await gotoLogin()
            return
          }
          await checkRole()
          setLoading(false)
        }

        // 権限を確認
        async function checkRole() {
          if (type !== Type.NotProtected) {
            if (!checkProtected(AuthStore.user, type)) {
              setError({ statusCode: 403 })
              return
            }
            await checkToken()
          }
        }

        async function checkCognitoUser() {
          if (!AuthStore.cognitoUser) {
            await AuthStore.restoreCognitoUser()
            if (!AuthStore.cognitoUser) {
              await gotoLogin()
              return
            }
          }
          setLoading(false)
        }

        async function checkToken() {
          const isTokenValid = await AuthStore.refreshTokenIfNeeded()
          if (!isTokenValid) {
            await gotoLogin(true)
            return
          }
        }

        if (type === Type.CognitoUser) {
          checkCognitoUser()
        } else if (needsCheck) {
          checkUser()
        } else if (type !== Type.NotProtected) {
          checkRole()
        }
      }, [])

      if (loading) {
        return <Loading />
      } else if (error) {
        return <Error statusCode={error.statusCode} errorMessage={error.message} />
      } else {
        return <Page {...props} />
      }
    }),
  )
}
